import { useCallback, useEffect, useState } from 'react'
import { ApiError, api, type Devices as DeviceList, type ReaderDevice, type RiskScore } from '../api'
import { Card, Chip, CopyButton, Countdown, Empty, Info, Loading, Modal, Pager, Qr, expiresIn, shortDate } from '../components'
import { vocabulary } from '../vocabulary'

const d = vocabulary.devices
const terms = vocabulary.terms
const glossary = vocabulary.glossary

/** The wireframe's bands: the table shows the band, not a bare number — Clear stays quiet. */
function riskBand(score: number): { label: string; kind?: 'ok' | 'wait' | 'stop' } {
  if (score >= 60) return { label: `${d.bandHigh} · ${score}`, kind: 'stop' }
  if (score >= 20) return { label: `${d.bandWatch} · ${score}`, kind: 'wait' }
  return { label: d.bandClear, kind: 'ok' }
}

const READERS_PER_PAGE = 8

// The Play internal-testing opt-in links — stable for each app's lifetime, so they ship as
// build-time config: a phone scans one ONCE to join the test programme, then every release arrives
// as a normal app update. Empty until the first release is live; the matching card hides itself.
const ADMIN_APP_OPTIN_URL: string = 'https://play.google.com/apps/internaltest/4699985324636957518'
const READER_APP_OPTIN_URL: string = 'https://play.google.com/apps/internaltest/4701736521698265885'

/**
 * Every device that belongs to the organization, in two groups that behave differently: the few
 * that approve, and the many that read. They live under two tabs because a reader fleet grows
 * large while admin devices stay a handful. Status is the column that decides what a person does
 * next, so it is a chip rather than a word buried in a row.
 */
export default function Devices({ accountId, canSetUp }: {
  accountId: string
  canSetUp: boolean
}) {
  const [data, setData] = useState<DeviceList | null>(null)
  const [risk, setRisk] = useState<Map<string, RiskScore> | null>(null)
  const [inspecting, setInspecting] = useState<ReaderDevice | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<'readers' | 'admins'>('readers')
  const [page, setPage] = useState(0)
  const [pairing, setPairing] = useState<{ qr: string; expiresAt: string } | null>(null)
  const [invite, setInvite] = useState<{ qr: string; token: string; expiresAt: string } | null>(null)
  const [inviteName, setInviteName] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    let live = true
    api.devices(accountId)
      .then((devices) => live && setData(devices))
      .catch((failure) => live && setError(
        failure instanceof ApiError ? failure.message : d.loadError))
    // Risk is decoration on this screen — a risk fetch failing must never break Devices.
    api.risk()
      .then((scores) => live && setRisk(new Map(scores.map((sc) => [sc.delegatee_device_id, sc]))))
      .catch(() => live && setRisk(new Map()))
    return () => { live = false }
  }, [accountId])

  useEffect(() => {
    setData(null)
    setError(null)
    return load()
  }, [load])

  async function pair() {
    setBusy(true)
    try {
      const minted = await api.pairingCode(accountId)
      setPairing({ qr: minted.qr_payload, expiresAt: minted.expires_at })
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : d.pairError)
    } finally {
      setBusy(false)
    }
  }

  async function createInvite() {
    setBusy(true)
    try {
      const minted = await api.readerInvite(accountId, inviteName.trim() || null)
      setInvite({ qr: minted.qr_payload, token: minted.invite_token, expiresAt: minted.expires_at })
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : d.inviteError)
    } finally {
      setBusy(false)
    }
  }

  if (error && !data) return <p className="banner stop">{error}</p>
  if (!data) return <Loading rows={5} />

  if (inspecting) {
    const score = risk?.get(inspecting.device_id)
    return (
      <RiskDetail
        device={inspecting}
        score={score ?? null}
        onBack={() => setInspecting(null)}
      />
    )
  }

  const readers = data.reader_devices
  const admins = data.admin_devices
  const pageCount = Math.max(1, Math.ceil(readers.length / READERS_PER_PAGE))
  const current = Math.min(page, pageCount - 1)
  const shown = readers.slice(current * READERS_PER_PAGE, current * READERS_PER_PAGE + READERS_PER_PAGE)

  return (
    <>
      <div className="stack" style={{ gap: 3 }}>
        <h1>{d.title}</h1>
        <p className="small muted">{d.subhead}</p>
      </div>

      {error && <p className="banner stop">{error}</p>}

      <div className="tabs" role="tablist" aria-label={d.deviceGroupsAria}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'readers'}
          className={tab === 'readers' ? 'tab on' : 'tab'}
          onClick={() => setTab('readers')}
        >
          {terms.readers} <span className="count nums">{readers.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'admins'}
          className={tab === 'admins' ? 'tab on' : 'tab'}
          onClick={() => setTab('admins')}
        >
          {d.adminsTab} <span className="count nums">{admins.length}</span>
        </button>
      </div>

      {tab === 'readers' ? (
        <Card
          title={<span>{terms.readerDevices} <Info term="reader" /></span>}
          action={canSetUp
            ? <button className="btn primary small" disabled={busy} onClick={() => { setInviteName(''); setInvite(null); createInvite() }}>{d.inviteDevice}</button>
            : undefined}
          footer={d.readersFooter}
        >
          {readers.length === 0 ? (
            <Empty
              line={d.noReaders}
              action={canSetUp
                ? <button className="btn small" disabled={busy} onClick={() => { setInviteName(''); createInvite() }}>{d.inviteDevice}</button>
                : <span className="tiny faint">{d.askSetup}</span>}
            />
          ) : (
            <div className="stack" style={{ gap: 16 }}>
              <div className="table-wrap">
                <table className="list">
                  <thead>
                    <tr><th>{d.colDevice}</th><th>{d.colJoined}</th><th>{d.colStatus}</th><th>{d.colRisk} <Info term="risk" /></th></tr>
                  </thead>
                  <tbody>
                    {shown.map((device) => (
                      <tr key={device.device_id}>
                        <td className="primary" data-label={d.colDevice}>
                          <div className="stack" style={{ gap: 1 }}>
                            <span>{device.name ?? device.assignment ?? d.unnamedDevice}</span>
                            {device.name && device.assignment
                              ? <span className="tiny faint">{device.assignment}</span>
                              : null}
                          </div>
                        </td>
                        <td data-label={d.colJoined} className="muted small">{shortDate(device.joined_at)}</td>
                        <td data-label={d.colStatus}>
                          {device.status === 'ACTIVE'
                            ? <Chip kind="ok">{d.statusActive}</Chip>
                            : device.status === 'REVOKED'
                              ? <Chip kind="stop">{d.statusRevoked}</Chip>
                              : <Chip kind="wait">{d.statusWaiting}</Chip>}
                        </td>
                        <td data-label={d.colRisk}>
                          {risk === null
                            ? <span className="tiny faint">…</span>
                            : (() => {
                                const score = risk.get(device.device_id)
                                if (!score) return <span className="tiny faint">—</span>
                                const band = riskBand(score.score)
                                return (
                                  <button
                                    type="button"
                                    className="chip-button"
                                    onClick={() => setInspecting(device)}
                                    aria-label={`${d.riskAriaPrefix} ${device.name ?? device.device_id}`}
                                  >
                                    <Chip kind={band.kind}>{band.label}</Chip>
                                  </button>
                                )
                              })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager page={current} pageCount={pageCount} onPage={setPage} />
            </div>
          )}
        </Card>
      ) : (
        <Card
          title={<span>{terms.adminDevices} <Info term="admin" /></span>}
          action={canSetUp
            ? <button className="btn small" disabled={busy} onClick={pair}>{d.pairDevice}</button>
            : undefined}
          footer={admins.filter((a) => a.status === 'ACTIVE').length < 2
            ? d.adminSecondFooter
            : undefined}
        >
          {admins.length === 0 ? (
            <Empty
              line={d.noAdmins}
              action={canSetUp
                ? <button className="btn small" disabled={busy} onClick={pair}>{d.pairDevice}</button>
                : <span className="tiny faint">{d.askSetup}</span>}
            />
          ) : (
            <div className="table-wrap">
              <table className="list">
                <thead>
                  <tr><th>{d.colDevice}</th><th>{d.colPaired}</th><th>{d.colStatus}</th></tr>
                </thead>
                <tbody>
                  {admins.map((device) => (
                    <tr key={device.key_id}>
                      <td className="primary" data-label={d.colDevice}>{device.name ?? terms.adminDevice}</td>
                      <td data-label={d.colPaired} className="muted small">{shortDate(device.paired_at)}</td>
                      <td data-label={d.colStatus}>
                        {device.status === 'ACTIVE'
                          ? <Chip kind="ok">{d.statusActive}</Chip>
                          : <Chip>{d.statusReplaced}</Chip>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'readers' && READER_APP_OPTIN_URL !== '' && (
        <Card
          title={d.getReaderApp}
          footer={d.appOptinFooter}
        >
          <div className="stack" style={{ gap: 12, alignItems: 'center' }}>
            <Qr payload={READER_APP_OPTIN_URL} download="reader-app-install" />
            <p className="small muted" style={{ margin: 0, textAlign: 'center', maxWidth: 420 }}>
              {d.readerAppInstall}
            </p>
          </div>
        </Card>
      )}

      {tab === 'admins' && ADMIN_APP_OPTIN_URL !== '' && (
        <Card
          title={d.getAdminApp}
          footer={d.appOptinFooter}
        >
          <div className="stack" style={{ gap: 12, alignItems: 'center' }}>
            <Qr payload={ADMIN_APP_OPTIN_URL} download="admin-app-install" />
            <p className="small muted" style={{ margin: 0, textAlign: 'center', maxWidth: 420 }}>
              {d.adminAppInstall}
            </p>
          </div>
        </Card>
      )}

      {pairing && (
        <Modal
          title={d.pairAdminTitle}
          onClose={() => { setPairing(null); load() }}
          footer={d.pairAdminFooter}
        >
          <div className="stack" style={{ gap: 16, alignItems: 'center' }}>
            <Qr payload={pairing.qr} download="admin-device-pairing" />
            <ol className="small muted" style={{ margin: 0, paddingLeft: '1.15em', lineHeight: 1.8 }}>
              <li>{d.pairStep1}</li>
              <li>{d.pairStep2}</li>
              <li>{d.pairStep3}</li>
            </ol>
            <div className="row">
              <span className="tiny faint"><Countdown to={pairing.expiresAt} prefix={d.codeExpiresIn} expired={d.codeExpired} /></span>
              <button className="btn small" disabled={busy} onClick={pair}>{d.newCode}</button>
            </div>
          </div>
        </Modal>
      )}

      {invite && (
        <Modal
          title={d.inviteReaderTitle}
          onClose={() => { setInvite(null); load() }}
          footer={d.inviteReaderFooter}
        >
          <div className="stack" style={{ gap: 12 }}>
            <label className="stack" style={{ gap: 6 }}>
              <span className="label">{d.labelThisDevice}</span>
              <div className="row">
                <input
                  className="input"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder={d.deviceNamePlaceholder}
                  maxLength={120}
                />
                <button className="btn small" disabled={busy} onClick={createInvite}>{d.rename}</button>
              </div>
            </label>

            <div className="stack" style={{ gap: 10, alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <Qr
                payload={invite.qr}
                download="reader-device-invite"
                actions={<CopyButton value={invite.qr} label={d.copyInvite} />}
              />
              <p className="tiny faint" style={{ textAlign: 'center', maxWidth: '40ch' }}>
                {d.shareInvite} {d.expiresWord} {expiresIn(invite.expiresAt)} {d.worksOnce}
              </p>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}


/**
 * The device risk panel (wireframe §10): the score with its factors — never one without the
 * other — and the generated briefing. High doesn't block anything by itself: approval and
 * revocation stay on the admin device, and this screen only ever explains.
 */
function RiskDetail({ device, score, onBack }: {
  device: ReaderDevice
  score: RiskScore | null
  onBack: () => void
}) {
  const name = device.name ?? device.assignment ?? d.unnamedDevice
  // The list hands us the materialized row; the panel re-scores live on open so what you inspect
  // is the freshest truth. Until (or if ever) the live score lands, the row stands in.
  const [live, setLive] = useState<RiskScore | null>(null)
  useEffect(() => {
    if (!score) return
    let alive = true
    api.riskOf(device.device_id)
      .then((fresh) => alive && setLive(fresh))
      .catch(() => {}) // risk is decoration — the materialized row is a fine stand-in
    return () => { alive = false }
  }, [device.device_id, score])
  const shown = live ?? score
  const band = shown ? riskBand(shown.score) : null
  return (
    <>
      <div className="stack" style={{ gap: 3 }}>
        <button type="button" className="btn small" style={{ alignSelf: 'flex-start' }} onClick={onBack}>{d.backToDevices}</button>
        <h1>{name} {d.riskTitleSuffix}</h1>
        <p className="small muted">{live ? d.computedNow : d.refreshing} {d.last14}</p>
      </div>

      {!shown ? (
        <Card title={glossary.risk.title}>
          <p className="small muted">{d.noActivity}</p>
        </Card>
      ) : (
        <>
          <Card title={<span>{glossary.risk.title} <Info term="risk" /></span>}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '2rem', fontVariantNumeric: 'tabular-nums' }}>
                {shown.score}<span className="tiny faint"> {d.riskOutOf}</span>
              </span>
              {band && <Chip kind={band.kind}>{band.label}</Chip>}
            </div>
          </Card>

          <Card title={d.whyTitle} footer={d.whyFooter}>
            {shown.factors.length === 0 ? (
              <p className="small muted">{d.noFactors}</p>
            ) : (
              <div className="stack" style={{ gap: 10 }}>
                {shown.factors.map((factor) => (
                  <div key={factor.rule} className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
                    <div className="stack" style={{ gap: 1 }}>
                      <span className="small">{ruleTitle(factor.rule)}</span>
                      <span className="tiny faint">{factor.detail}</span>
                    </div>
                    <span className="small nums" style={{ flex: 'none' }}>+{factor.points}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {shown.factors.length > 0 && <SummaryCard deviceId={device.device_id} />}

          <p className="tiny faint">{d.highNoBlock}</p>
        </>
      )}
    </>
  )
}

/** The risk-engine rule keys, said the way this screen says things — copy lives in the vocabulary. */
function ruleTitle(rule: string): string {
  return vocabulary.riskRules[rule] ?? rule
}

/**
 * The generated briefing (wireframe §10, mark 4): labeled, sourced ("written from the factors
 * above — nothing else"), regenerable, advisory. A 503 means summaries aren't configured — the
 * card simply doesn't render; the factors are the record either way.
 */
function SummaryCard({ deviceId }: { deviceId: string }) {
  const [summary, setSummary] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const [busy, setBusy] = useState(false)

  const fetchSummary = useCallback((refresh: boolean) => {
    setBusy(true)
    api.riskSummary(deviceId, refresh)
      .then((answer) => { setSummary(answer.summary); setUnavailable(false) })
      .catch(() => setUnavailable(true))
      .finally(() => setBusy(false))
  }, [deviceId])

  useEffect(() => { fetchSummary(false) }, [fetchSummary])

  if (unavailable) return null
  return (
    <Card title={d.summaryTitle} action={<span className="tiny faint">{d.generated}</span>}>
      {summary === null ? (
        <Loading rows={2} />
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          <p className="small muted" style={{ margin: 0 }}>{summary}</p>
          <div className="row" style={{ gap: 10 }}>
            <span className="tiny faint">{d.writtenFrom}</span>
            <button className="btn small" disabled={busy} onClick={() => fetchSummary(true)}>
              {busy ? d.working : d.regenerate}
            </button>
          </div>
        </div>
      )}
    </Card>
  )
}
